/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Abilities from "./Abilities";
import Armor from "./Armor";
import Attributes from "./Attributes";
import AttributeValues from "./AttributeValues";
import ImageData from "./ImageData";
import NpcData from "./NpcData";
import ObjectHelper from "./ObjectHelper";
import SensesObject from "./SensesObject";
import SkillSet, { Skill } from "./SkillSet";
import SkillStats from "./SkillStats";
import Speed from "./Speed";
import TetraCube from "./TetraCube";
import Utils from "./Utils";
import JSZip from "jszip";
import { saveAs } from "file-saver";

export class DocRef {
    slug:                   string = '';
    page_no:                number = 0;
    document__slug:         string = '';
    document__title:        string = '';
    document__license_url:  string = '';
    document__url:          string = '';
}

class Monster {
    name:                   string = 'Default Monster';
    shortName:              string = '';
    pluralName:             string = '';
    desc:                   string = '';
    size:                   string = 'Medium';
    type:                   string = 'Humanoid';
    subtype:                string = '';
    group:                  string = '';
    alignment:              string = 'Neutral';
    armor:                  Armor | null = null;
    armor_class:            number = 10;
    armor_desc:             string = '';
    hit_points:             number = 5;
    hit_dice:               string = '1d8'; /*16d10+64*/
    hitDiceNumber:          number = 1;
    hitDiceType:            number = 8;
    hitDiceBonus:           number = 0;
    hitPointsCustom:        string = '';
    usePcHitDice:           boolean = false;
    speed:                  Speed  = new Speed();
    attributes:             AttributeValues = new AttributeValues();
    saving_throws:          string = '';
    damage_vulnerabilities: string = '';
    damage_resistances:     string = '';
    damage_immunities:      string = '';
    condition_immunities:   string = '';
    gear:                   string = '';
    senses:                 string = ''; /*blindsight 30 ft., darkvision 120 ft., passive Perception 19'*/
    sensesObject:           SensesObject = new SensesObject();
    passivePerception:      number = 10;
    languages:              string = 'Common'; /*'Common, Draconic',*/
    skillSet:               SkillSet = new SkillSet();
    challenge_rating:       string = '1';
    cr:                     number = 1.0;
    xp:                     number = 200;
    proficiencyBonus:       number = 2;
    actions:                Ability[] = []; /*[{name, desc, attack_bonus, damage_dice, damage_bonus}, ...]*/
    spells:                 Ability[] = [];
    bonus_actions:          Ability[] = []; /*null*/
    reactions:              Ability[] = [];
    legendary_desc:         string    = '';
    legendary_actions:      Ability[] = [];
    traits:                 Ability[] = [];
    spell_list:             string[]  = [];
    environments:           string[]  = []; /*['Desert','Coastal'],*/
    inventory:              any[]     = [];
    mythic_desc:            string    = '';
    mythic_actions:         any[]     = [];
    regional_desc:          string    = '';
    regional_desc_end:      string    = '';
    regional_actions:       any[]     = [];
    lair_desc:              string    = '';
    lair_desc_end:          string    = '';
    lair_actions:           any[]     = [];
    doubleColumns:          boolean   = true;
    splitPoint:             number    = -1;
    extraWhitespace:        number    = 0;
    image:                  ImageData = new ImageData();
    img_main:               string | null = null;
    npcData:                NpcData   = new NpcData();
    doc_ref:                DocRef    = new DocRef();
    file_type:              string    = 'dnd monster';
    file_version:           number    = 2;

    static validate(monster: Monster): void {
        // This method makes sure that any old version of the monster is updated to the latest version.
        if (!monster.sensesObject || SensesObject.isBlank(monster.sensesObject)) {
            // There is a bug that writes the passive perception wrong. Correct it here.
            const percSkill = monster.skillSet.skills['Perception'];
            const passivePerception = 10 + (percSkill?.adjLevel ?? Attributes.getBonus(monster.attributes.wisdom));
            monster.sensesObject = SensesObject.parseFromString(monster.senses);
            monster.sensesObject.passiveperception = passivePerception;
        }
    }

    static convertFromJson(jsonObject: any): Monster {
        const monster = new Monster();
        ObjectHelper.copyPropertiesToObject(jsonObject, monster);

        // convert any old version to the newest format:
        monster.image.src            = jsonObject.imageSrc                      ?? jsonObject.image?.src            ?? monster.image.src;
        monster.image.width          = parseInt(jsonObject.imageWidth           ?? jsonObject.image?.width          ?? monster.image.width);
        monster.image.opacity        = parseFloat(jsonObject.imageOpacity       ?? jsonObject.image?.opacity        ?? monster.image.opacity);
        monster.image.positionRight  = parseInt(jsonObject.imagePositionRight   ?? jsonObject.image?.positionRight  ?? monster.image.positionRight);
        monster.image.positionBottom = parseInt(jsonObject.imagePositionBottom  ?? jsonObject.image?.positionBottom ?? monster.image.positionBottom);
        monster.image.rotation       = parseInt(jsonObject.imageRotation        ?? jsonObject.image?.rotation       ?? monster.image.rotation);
        monster.image.horizontalFlip = !!(jsonObject.imageHorizontalFlip        ?? jsonObject.image?.horizontalFlip ?? monster.image.horizontalFlip);
        monster.image.behind         = !!(jsonObject.imageBehind                ?? jsonObject.image?.behind         ?? monster.image.behind);
        monster.image.clip           = !!(jsonObject.imageClip                  ?? jsonObject.image?.clip           ?? monster.image.clip);

        monster.attributes.strength     = jsonObject.strPoints ?? jsonObject.strength     ?? monster.attributes.strength;
        monster.attributes.dexterity    = jsonObject.dexPoints ?? jsonObject.dexterity    ?? monster.attributes.dexterity;
        monster.attributes.constitution = jsonObject.conPoints ?? jsonObject.constitution ?? monster.attributes.constitution;
        monster.attributes.intelligence = jsonObject.intPoints ?? jsonObject.intelligence ?? monster.attributes.intelligence;
        monster.attributes.wisdom       = jsonObject.wisPoints ?? jsonObject.wisdom       ?? monster.attributes.wisdom
        monster.attributes.charisma     = jsonObject.chaPoints ?? jsonObject.charisma     ?? monster.attributes.charisma;
        monster.traits = jsonObject.traits ?? jsonObject.special_abilities;

        if (jsonObject.skillSet) {
            monster.skillSet = jsonObject.skillSet;
        } else {
            if (jsonObject.skills) {
                //"skills":[{"name":"perception","stat":"wis"},
                //          {"name":"stealth","stat":"dex","note":" (ex)"},
                //          {"name":"survival","stat":"wis","note":" (ex)"}],
                if (jsonObject.skills.length) {
                    for (const skill of jsonObject.skills) {
                        const newSkill = new Skill();
                        const attribute = Attributes.getAttrName(skill.stat);
                        if (attribute != '') {
                            newSkill.attribute = attribute;
                        }
                        if (skill.notes?.includes(' (ex)') || skill.notes?.toLowerCase().includes(' (expert)')) {
                            newSkill.level = 'expert';
                        } else {
                            newSkill.level = 'proficient';
                        }
                        monster.skillSet.skills[skill.name] = newSkill;
                    }
                }
            }
            for (const set of [{ skills: jsonObject.expertSkills, level: 'expert' },
                               { skills: jsonObject.proficientSkills, level: 'proficient' }]) {
                if (set.level !== 'expert' && set.level !== 'proficient') {
                    continue;
                }
                for (const skillName of set.skills || []) {
                    const sk = monster.skillSet.skills[skillName];
                    if (sk != null) {
                        sk.level = set.level;
                    } else {
                        const attribute = SkillStats.getSkillsMap().get(skillName);
                        if (attribute) {
                            const newSkill = new Skill();
                            newSkill.attribute = attribute;
                            newSkill.level = set.level;
                            monster.skillSet.skills[skillName] = newSkill;
                        }
                    }
                }
            }
        }

        monster.skillSet = SkillSet.updateLevels(monster.skillSet, monster.attributes, jsonObject.proficiencyBonus);

        const percSkill = monster.skillSet.skills['Perception'];
        monster.passivePerception = 10 + (percSkill?.adjLevel ?? Attributes.getBonus(monster.attributes.wisdom));

        // Languages:
        if (typeof jsonObject.languages !== 'string') {
            monster.languages = TetraCube.parseLanguages(jsonObject);
        }

        if (!jsonObject.gear) {
            const newGear = [];
            const possibleGear = Abilities.getGearNames();
            for (const abilities of [jsonObject.actions, jsonObject.inventory]) {
                for (const ability of abilities || []) {
                    if (possibleGear.includes(ability.name)) {
                        newGear.push(ability.name);
                    }
                }
            }
            monster.gear = newGear.join(', ');
        }
        return monster;
    }

    static accessValueAtPath = (obj: any, pathStr: string, valueToSet: any): any => {
        // Validate path to prevent prototype pollution
        if (!pathStr || typeof pathStr !== 'string') {
            return undefined;
        }

        const path = pathStr.split('.');

        // Check for dangerous paths
        const dangerousProps = ['__proto__', 'constructor', 'prototype'];
        if (path.some(key => dangerousProps.includes(key))) {
            console.error('Attempt to access dangerous property path:', pathStr);
            return undefined;
        }

        const currentKey = path[0];

        // If we've reached the last key in the path
        if (path.length === 1) {
            const previousValue = obj[currentKey];  // Store the previous value

            // If a value to set is provided, set it
            if (valueToSet !== undefined) {
                if (typeof obj[currentKey] === 'number' && currentKey !== 'hitPointsCustom') {
                    valueToSet = parseFloat(valueToSet);
                }
                obj[currentKey] = valueToSet;
            }

            // Return the previous value
            return previousValue;
        }
        // If the next object does not exist, create it if we are setting a value
        if (!obj[currentKey]) {
            if (valueToSet === undefined) {
                return undefined;  // If getting a value and the path doesn't exist, return undefined
            }
            obj[currentKey] = {};
        }

        // Recurse with the next part of the path
        return this.accessValueAtPath(obj[currentKey], path.slice(1).join('.'), valueToSet);
    }

    static updateMonster = (monster: Monster, field: string, value: any, forceUpdate: boolean): Monster => {
        const previousValue: any = this.accessValueAtPath(monster, field, undefined);

        if (typeof previousValue === 'number' && field !== 'hitPointsCustom') {
            value = parseFloat(value);
        }
        // Unless we are force updating (from a recursion call), bail out early if nothing changed
        if (!forceUpdate) {
            if (previousValue === value) {
                return monster;
            }
            if (typeof value == 'object' && JSON.stringify(previousValue) === JSON.stringify(value)) {
                return monster;
            }
        }

        if (field === 'proficiencyBonus' && (value < 1 || value > 10)) {
            return this.updateMonster(monster, field, Math.min(Math.max(value, 1), 10), forceUpdate);
        }

        console.log(`updateMonster, field '${field}' to '${JSON.stringify(value)}'`);
        const isAttr = field.startsWith('attributes.');
        if (isAttr && (value < 1 || value > 30)) {
            value = Math.min(Math.max(value, 1), 30);
        }
        if (field === 'image.positionRight') {
            value = Math.min(Math.max(value, -500), 1000);
        }
        if (field === 'image.positionBottom') {
            value = Math.min(Math.max(value, -300), 500);
        }
        if (field === 'hitDiceNumber') {
            value = Math.min(Math.max(value, 1), 50);
        }

        // clone the monster:
        monster = { ...monster };
        // set the value:
        this.accessValueAtPath(monster, field, value);

        // now check for side effects:
        if (isAttr || field === 'proficiencyBonus') {
            const newSkillSet = SkillSet.updateLevels(monster.skillSet, monster.attributes, monster.proficiencyBonus);
            monster = this.updateMonster(monster, 'skillSet', newSkillSet, forceUpdate);
        }
        if (field === 'challenge_rating') {
            monster = this.updateMonster(monster, 'cr', Utils.parseCrString(monster.challenge_rating), forceUpdate);
        } else if (field === 'cr') {
            monster = this.updateMonster(monster, 'proficiencyBonus', Utils.getProfBonusByCR(monster.cr), forceUpdate);
            monster = this.updateMonster(monster, 'xp', Utils.getXpByCR(monster.cr), forceUpdate);
        } else if (field === 'size') {
            monster = this.updateMonster(monster, 'hitDiceType', Utils.getHitDieBySize(monster.size), forceUpdate);
        } else if (field === 'attributes.dexterity') {
            const oldAc = Armor.getAcFromDexAndArmorDesc(previousValue as number, monster.armor_desc);
            if (oldAc === monster.armor_class) {
                const newAc = Armor.getAcFromDexAndArmorDesc(value, monster.armor_desc);
                monster = this.updateMonster(monster, 'armor_class', newAc, forceUpdate);
            }
        } else if (field === 'attributes.constitution') {
            monster = this.updateMonster(monster, 'hitDiceBonus', Attributes.getBonus(monster.attributes.constitution) * monster.hitDiceNumber, true /*forceUpdate*/);
        } else if (['attributes.wisdom', 'proficiencyBonus', 'skillSet'].includes(field)) {
            const percSkill = monster.skillSet.skills['Perception'];
            const passivePerception = 10 + (percSkill?.adjLevel ?? Attributes.getBonus(monster.attributes.wisdom));
            monster = this.updateMonster(monster, 'passivePerception', passivePerception, forceUpdate);
        } else if (field === 'hitDiceNumber') {
            const bonus = Attributes.getBonus(monster.attributes.constitution as number) * monster.hitDiceNumber;
            monster = this.updateMonster(monster, 'hitDiceBonus', bonus, true /*forceUpdate*/);
        } else if (['hitDiceType', 'hitDiceBonus', 'usePcHitDice'].includes(field)) {
            // monster hit dice use dice average (d4=2.5, d6=3.5, d8=4.5, etc) per level, and then round up (d8 per level: 5, 9, 14, 18, 23, 27...)
            let hit_points = Math.max(1, Math.round(monster.hitDiceNumber * (monster.hitDiceType + 1) / 2) + monster.hitDiceBonus);
            if (monster.usePcHitDice) {
                // PC hit dice use max roll at level one, and then round up (d4=3, d6=4, d8=5) at each level (d8 per level: 8, 13, 18, 23, 28, 33...)
                hit_points = monster.hitDiceType + monster.hitDiceBonus + (monster.hitDiceNumber-1) * (monster.hitDiceType/2 + 1);
            }
            let hit_dice = `${monster.hitDiceNumber}d${monster.hitDiceType}`;
            if (monster.hitDiceBonus > 0) {
                hit_dice += `+${monster.hitDiceBonus}`;
            } else if (monster.hitDiceBonus < 0) {
                hit_dice += monster.hitDiceBonus.toString();
            }
            monster = this.updateMonster(monster, 'hit_points', hit_points, forceUpdate);
            monster = this.updateMonster(monster, 'hit_dice', hit_dice, forceUpdate);
            monster = this.updateMonster(monster, 'hitPointsCustom', `${hit_points} (${hit_dice})`, forceUpdate);
        } else if (field === 'name') {
            if (monster.shortName === previousValue) {
                monster = this.updateMonster(monster, 'shortName', value, forceUpdate);
            } else if (monster.shortName === (previousValue as string).toLowerCase()) {
                monster = this.updateMonster(monster, 'shortName', value.toLowerCase(), forceUpdate);
            } else {
                monster = this.updateMonster(monster, 'shortName', '', forceUpdate);
            }
            if (monster.pluralName === `${previousValue}s`) {
                monster = this.updateMonster(monster, 'pluralName', `${value}s`, forceUpdate);
            } else if (monster.pluralName === `${(previousValue as string).toLowerCase()}s`) {
                monster = this.updateMonster(monster, 'pluralName', `${value.toLowerCase()}s`, forceUpdate);
            } else {
                monster = this.updateMonster(monster, 'pluralName', '', forceUpdate);
            }
        } else if (field === 'armor_desc') {
            const newHasShield = value?.toLowerCase().includes('shield') || false;
            // add or remove the shield to/from the monster's gear
            const newGear = new Set<string>(monster.gear?.trim().split(/ *, */) || []);
            if (newHasShield && !newGear.has('Shield') && !newGear.has('shield')) {
                newGear.add('Shield');
            } else {
                newGear.delete('Shield');
                newGear.delete('shield');
            }
            monster.gear = Array.from(newGear).filter(o => o).join(", ");
        } else if (field === 'armor.name' || field === 'armor') {
            // add the armor to the monster's gear
            const newGear = new Set<string>(monster.gear?.trim().split(/ *, */) || []);
            if (field === 'armor.name') {
                // try to avoid adding gear such as 'Natural Armor' or 'Barkskin', but allow custom armors
                const armor = Armor.getArmor(value);
                if (!armor || (armor.type !== "special" && armor.type !== "minAc")) {
                    newGear.add(value);
                }
                newGear.delete(previousValue);
            } else {
                // try to avoid adding gear such as 'Natural Armor' or 'Barkskin', but allow custom armors
                if (value.type !== "special" && value.type !== "minAc") {
                    newGear.add(value.name);
                }
                newGear.delete(previousValue?.name);
            }
            monster.gear = Array.from(newGear).filter(o => o).join(", ");
        } else if (field === 'actions') {
            const gearNames = Abilities.getGearNames();
            const newNames = monster.actions.map((o)=>o.name).filter(o => gearNames.includes(o));
            const oldNames = previousValue.map( (o: any) => o.name).filter((o: any) => gearNames.includes(o));
            const addedNames = Monster.subtractStringSets(newNames, oldNames);
            const removedNames = Monster.subtractStringSets(oldNames, newNames);
            // add the armor to the monster's gear
            const newGear = new Set<string>(monster.gear?.trim().split(/ *, */) || []);
            for (const addedName of addedNames) {
                newGear.add(addedName);
            }
            for (const removedName of removedNames) {
                newGear.delete(removedName);
            }
            monster.gear = Array.from(newGear).filter(o => o).join(", ");
        } else if (field === 'inventory') {
            const gearNames = Abilities.getMagicGearNames();
            const newNames = monster.inventory.map((o)=>o.name).filter(o => gearNames.includes(o));
            const oldNames = previousValue.map( (o: any) => o.name).filter((o: any) => gearNames.includes(o));
            const addedNames = Monster.subtractStringSets(newNames, oldNames);
            const removedNames = Monster.subtractStringSets(oldNames, newNames);
            // add the armor to the monster's gear
            const newGear = new Set<string>(monster.gear?.trim().split(/ *, */) || []);
            for (const addedName of addedNames) {
                newGear.add(addedName);
            }
            for (const removedName of removedNames) {
                newGear.delete(removedName);
            }
            monster.gear = Array.from(newGear).filter(o => o).join(", ");
        }

        // Whenever we change the name, its document reference is no longer valid.
        if (field == 'name') {
            monster.doc_ref = {
                slug:'',
                page_no: 0,
                document__slug:'',
                document__title:'',
                document__license_url:'',
                document__url:''
            };
        }

        return monster;
    }

    static subtractStringSets = (setA: string[], setB: string[]): string[] => {
        const setBSet = new Set(setB); // Convert setB to a Set for efficient lookup
        return setA.filter((str: string) => !setBSet.has(str));
    }


    static getMonsterXml = (monster: Monster): string => {
        if (!monster.name?.trim()) {
            alert("Monster must have a name before export!");
            return '';
        }

        const attrFunc = (attrName: string, attrVal: number): string => {
            const bonus = Attributes.getBonus(attrVal);
            return `
                <${attrName}>
                    <bonus type="number">${bonus}</bonus>
                    <score type="number">${attrVal}</score>
                </${attrName}>`;
        }
        const attrsFunc = (attributes: AttributeValues): string => {
            return attrFunc('charisma', attributes.charisma)
                 + attrFunc('constitution', attributes.constitution)
                 + attrFunc('dexterity', attributes.dexterity)
                 + attrFunc('intelligence', attributes.intelligence)
                 + attrFunc('strength', attributes.strength)
                 + attrFunc('wisdom', attributes.wisdom);
        }

        const getAbility = (ability: Ability, id: string): string => {
            return `
                <${id}>
                    <name type="string">${Monster.prepareText(ability.name, monster)}</name>
                    <desc type="formattedtext">${Monster.prepareText(ability.desc, monster)}</desc>
                </${id}>
            `
        }
        const getAbilities = (attrs: Ability[]): string => {
            let idNum = 1;
            let response = '';
            for (const attr of attrs) {
                let idStr = '00000' + idNum;
                idStr = idStr.substring(idStr.length - 5); // last 5 characters
                response += getAbility(attr, 'id-' + idStr);
            }
            return response;
        }


        const getSpellSlots = (monster: Monster): string => {
            let response = '';
            for (let level = 1 ; level < 10 ; level++) {
                let slots = 0;
                // TODO: how do I get this?
                response += `<level${level} type="number">${slots}</level${level}>`
            }
            return response;
        }
        return `
    <root version="5" dataversion="20250919" release="8.1|CoreRPG:7">
        <npc>
            <abilities>${attrsFunc(monster.attributes)}
            </abilities>
            <ac type="number">${monster.armor_class}</ac>
            <actext type="string">(${Monster.prepareText(monster.armor_desc, monster)})</actext>
            <actions>${getAbilities(monster.actions)}</actions>
            <alignment type="string">${Monster.prepareText(monster.alignment, monster)}</alignment>
            <bonusactions>${getAbilities(monster.bonus_actions)}</bonusactions>
            <conditionimmunities type="string">${Monster.prepareText(monster.condition_immunities, monster)}</conditionimmunities>
            <cr type="string">${monster.cr}</cr>
            <damageimmunities type="string">${Monster.prepareText(monster.damage_immunities, monster)}</damageimmunities>
            <damageresistances type="string">${Monster.prepareText(monster.damage_resistances, monster)}</damageresistances>
            <damagethreshold type="number">0</damagethreshold>
            <hd type="string">${Monster.prepareText(monster.hit_dice, monster)}</hd>
            <hp type="number">${monster.hit_points}</hp>
            <innatespells/>
            <lairactions>${getAbilities(monster.lair_actions)}</lairactions>
            <languages type="string">${Monster.prepareText(monster.languages, monster)}</languages>
            <legendaryactions/>
            <locked type="number">0</locked>
            <name type="string">${Monster.safeKey(monster.name)}</name>
            ${monster.image?.src ? `<picture type="token">${monster.image.src}</picture>` : '<picture/>'}
            <reactions>${getAbilities(monster.reactions)}</reactions>
            <savingthrows type="string">${monster.saving_throws}</savingthrows>
            <senses type="string">${monster.senses}</senses>
            <size type="string">${monster.size}</size>
            <skills type="string">${Monster.prepareText(SkillSet.getString(monster.skillSet), monster)}</skills>
            <speed type="string">${Monster.prepareText(Monster.formatSpeed(monster.speed), monster)}</speed>
            <spells>${getAbilities(monster.spells)}</spells>
            <spellslots>${getSpellSlots(monster)}</spellslots>
            <text type="formattedtext">${Monster.prepareText(monster.desc, monster)}</text>
            ${monster.image?.src ? `<token type="token">${monster.image.src}</token>`:`<token/>`}
            ${monster.image?.src ? `<token3Dflat type="token">${monster.image.src}</token3Dflat>` : `<token3Dflat/>`}
            <traits>${getAbilities(monster.traits)}</traits>
            <type type="string">${Monster.prepareText(monster.type, monster)}</type>
            <xp type="number">${monster.xp}</xp>
        </npc>
    </root>`.trim();
    }

    static exportMonsterToXml = (monster: Monster) => {
        const content = this.getMonsterXml(monster);
        // === Generate .xml file ===
        const filename = `${Monster.sanitizeFilename(monster.name)}.xml`;
        const blob = new Blob([content], { type: "text/xml;charset=utf-8" });
        saveAs(blob, filename);
    }

    static exportMonsterToMod = async (monster: Monster) => {
        // === db.xml ===
        const dbXml = this.getMonsterXml(monster);

        // === definition.xml ===
        const definitionXml = `
    <root>
        <name>${Monster.prepareText(monster.name, monster)}</name>
        <author>Generated by Green God Games Monster Forge</author>
        <category>Monsters</category>
        <ruleset>5E</ruleset>
        <description>${Monster.prepareText(monster.desc || "Custom monster module", monster)}</description>
    </root>`.trim();

        // === Add to ZIP ===
        const zip = new JSZip();
        zip.file("definition.xml", definitionXml);
        zip.file("db.xml", dbXml);

        // Optional thumbnail
        if (monster.image?.src) {
            zip.file("thumbnail.png", monster.image.src.split(',')[1], { base64: true });
        }

        // === Generate .mod file ===
        const content = await zip.generateAsync({ type: "blob" });
        const filename = `${Monster.sanitizeFilename(monster.name)}.mod`;
        saveAs(content, filename);
    }

    // --- helper functions ---
    static formatAbilities = (monster: Monster): string => {
        return Monster.formatAbilitiesBlock(monster, monster.traits, 'trait') + 
               Monster.formatAbilitiesBlock(monster, [...monster.actions, ...monster.spells], 'action') +
               Monster.formatAbilitiesBlock(monster, monster.reactions, 'reaction') +
               Monster.formatAbilitiesBlock(monster, monster.legendary_actions, 'legendaryaction') +
               Monster.formatAbilitiesBlock(monster, monster.lair_actions, 'lairaction');
    }

    static formatAbilitiesBlock = (monster: Monster, abilities: Ability[], blockName: string): string => {
        if (!abilities || !abilities.length) {
            return '';
        }
        let blockXml = `<${blockName}s>`;
        abilities.forEach((ability) => {
            const name = Monster.prepareText(ability.name ?? '', monster);
            const desc = Monster.prepareText(ability.desc ?? '', monster);
            blockXml += `<${blockName}><name type="string">${name}</name><desc type="string">${desc}</desc></${blockName}>`;
        });
        blockXml += `</${blockName}s>\n`;
        return blockXml;
    }

    static formatSpeed = (speed: Speed): string => {
        // adapt to your Speed object shape; this covers common fields
        const parts: string[] = [];
        if (speed.walk !== undefined && speed.walk !== null && String(speed.walk).length) parts.push(`${speed.walk} ft.`);
        if (speed.burrow) parts.push(`burrow ${speed.burrow} ft.`);
        if (speed.climb) parts.push(`climb ${speed.climb} ft.`);
        if (speed.fly) parts.push(`fly ${speed.fly} ft.`);
        if (speed.swim) parts.push(`swim ${speed.swim} ft.`);
        if (speed.hover) parts.push(`hover`);
        return parts.join(", ");
    }

    static prepareText = (source: string, monster: Monster): string => {
        source = Abilities.ReplaceTags(source, monster);
        let result = (source ?? '').replace(/&/g, "&amp;")
                                   .replace(/</g, "&lt;")
                                   .replace(/>/g, "&gt;")
                                   .replace(/"/g, "&quot;")
                                   .replace(/'/g, "&apos;");       
        result = result.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")// Convert **bold**
                       .replace(/__(.*?)__/g, "<b>$1</b>")    // Convert __bold__
                       .replace(/\*(.*?)\*/g, "<i>$1</i>")    // Convert *italic*
                       .replace(/_(.*?)_/g, "<i>$1</i>")      // Convert _italic_
                       .replace(/\n\s*\n/g, "<p/>")     // Convert double line breaks into FG paragraphs
                       .replace(/\n/g, "<br/>");        // Convert single line breaks into <br/>
        //console.log(`prepareText('${source}) => ${result}`);
        return result.trim();
    }

    static safeKey = (name: string): string => {
        // ensure it starts with a letter and contains only [a-z0-9_]
        let key = name.toLowerCase().replace(/[^a-z0-9_]+/g, "_");
        if (!/^[a-z]/.test(key)) key = `m_${key}`;
        return key;
    }

    static sanitizeFilename = (name: string): string => {
        return name.replace(/[^a-z0-9_\- ]+/gi, "_");
    }
}
export default Monster;